import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RefreshCw, AlertCircle, DollarSign, ListChecks, ScrollText, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt, statusBadge } from './_shared';

export function OverviewTab({ statementMonth, onMonthChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statementMonth ? { statement_month: statementMonth } : {};
      const r = await compensationAPI.summary(params);
      setData(r.data);
    } catch {
      toast.error('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [statementMonth]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="grid gap-3 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}><CardContent className="p-6"><div className="h-16 animate-pulse bg-zinc-100 rounded" /></CardContent></Card>
        ))}
      </div>
    );
  }

  const tiles = [
    { label: 'Records', value: data.record_count, icon: ListChecks, hint: data.statement_month || 'All months' },
    { label: 'Expected', value: fmt(data.total_expected), icon: DollarSign, hint: 'Sum across records' },
    { label: 'Actual Paid', value: fmt(data.total_actual_paid), icon: DollarSign, hint: 'Sum across records' },
    { label: 'Variance', value: fmt(data.variance), icon: AlertCircle, hint: 'Expected − Actual', warn: data.variance > 0 },
    { label: 'Final Compensation', value: fmt(data.total_final_compensation), icon: Calculator, hint: 'Computed payouts' },
    { label: 'Open Reviews', value: data.open_reviews, icon: AlertCircle, hint: 'Need attention', warn: data.open_reviews > 0 },
    { label: 'Pending Adjustments', value: data.pending_adjustments, icon: ScrollText, hint: 'Next-cycle ledger' },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label className="text-sm text-zinc-600">Filter month:</Label>
        <Input
          data-testid="comp-summary-month-filter"
          type="month"
          value={statementMonth || ''}
          onChange={(e) => onMonthChange(e.target.value)}
          className="w-44"
        />
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="comp-summary-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className={t.warn ? 'border-amber-300' : ''}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase text-zinc-500 tracking-wide">{t.label}</div>
                  <div className="text-2xl font-semibold mt-1" data-testid={`comp-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>{t.value}</div>
                  <div className="text-xs text-zinc-500 mt-1">{t.hint}</div>
                </div>
                <t.icon className={`h-5 w-5 ${t.warn ? 'text-amber-600' : 'text-zinc-400'}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {data.by_status && Object.keys(data.by_status).length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Records by Status</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {Object.entries(data.by_status).map(([s, n]) => (
              <div key={s} className="flex items-center gap-2">{statusBadge(s)}<span className="text-sm">{n}</span></div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
